import {
    Approved,
    DecisionDataStore,
    Reviewing,
    ApplicationState,
    SecondLookState,
    Declined,
    UnderReview,
    CounterOffer,
} from '@servicetitan/financing-decision-shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    SubmitApplicationDecision,
    type GetLoanApiResponse,
} from '@servicetitan/financing-api-client';
import { SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import {
    WorkflowStore,
    SetupDataStore,
    useSecondLookIntegration,
    useProviderConfig,
} from '@servicetitan/financing-workflow-shared';
import { useLoanSubmission } from './hooks/useLoanSubmission';
import { useFinancingPrequalSubmission as useFinancingPrequalSubmissionHook } from './hooks/useFinancingPrequalSubmission';
import { useSecondLookSubmission } from './hooks/useSecondLookSubmission';
import { Flex } from '@servicetitan/anvil2';
import { useLoanStatusPolling } from './hooks/useLoanStatusPolling';
import { useLoanRefresh } from './hooks/useLoanRefresh';
import { usePrequalificationHardSubmission } from './hooks/usePrequalificationHardSubmission';
import { useGreenSkyIovation } from '@servicetitan/financing-application-shared';
import { ReviewingFooterStore } from '../layout';

const ReviewingWorkflowInner = () => {
    const { decisionData, setDecisionData } = DecisionDataStore.useStore();
    const [applicationState, setApplicationState] = useState<ApplicationState>('sending');
    const [secondLookState, setSecondLookState] = useState<SecondLookState>('none');
    const [secondLookDecision, setSecondLookDecision] = useState<SubmitApplicationDecision>(
        SubmitApplicationDecision.Submitted,
    );
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const secondLookIntegration = useSecondLookIntegration();
    const { setSelectedPlan } = SelectedPlanStore.useStore();
    const { setShowApprovedFooter } = ReviewingFooterStore.useStore();
    const isHardPrequalification = setupData?.prequalificationId != null;
    const isGreenSky = setupData?.provider?.toLowerCase() === 'greensky';
    const { fingerprint, iovationComponent } = useGreenSkyIovation();
    const { useDecisionWorkflow, useFinancingPrequalSubmission } = useProviderConfig();

    const isFinancingSoftPrequal =
        useFinancingPrequalSubmission === true &&
        !setupData?.hasTransitionedToHardApp &&
        !isHardPrequalification;

    const {
        isSuccess: isLoanSubmissionSuccess,
        prequalId,
        loanId,
    } = useLoanSubmission({
        autoSubmit: !isHardPrequalification && !isFinancingSoftPrequal,
        fingerprint,
        onSuccess: () => {
            setApplicationState('reviewing');
        },
    });

    const { isSuccess: isFinancingPrequalSuccess, loanId: financingPrequalLoanId } =
        useFinancingPrequalSubmissionHook({
            autoSubmit: isFinancingSoftPrequal,
            enabled: isFinancingSoftPrequal,
            fingerprint,
            onSuccess: () => {
                setApplicationState('reviewing');
            },
        });

    const { isSuccess: isPrequalSubmissionSuccess, loanId: prequalLoanId } =
        usePrequalificationHardSubmission({
            autoSubmit: isHardPrequalification,
            prequalId: setupData?.prequalificationId ?? '',
            fingerprint,
            onSuccess: () => {
                setApplicationState('reviewing');
            },
        });

    useLoanRefresh({
        isSubmissionSuccess:
            isPrequalSubmissionSuccess || isLoanSubmissionSuccess || isFinancingPrequalSuccess,
        prequalId,
        isHardPrequalification,
        loanId: loanId ?? prequalLoanId ?? financingPrequalLoanId,
    });

    // Second look submission hook - only submitted when main decision is declined
    const {
        submitSecondLook,
        loanId: secondLookLoanId,
        isBelowWaterfallThreshold,
        tenantSettingsLoaded,
    } = useSecondLookSubmission({
        autoSubmit: false,
        onSuccess: () => {
            setSecondLookState('reviewing');
        },
    });

    const decisionDataRef = useRef(decisionData);
    decisionDataRef.current = decisionData;

    const handleSecondLookDecisionChange = useCallback(
        (decision: SubmitApplicationDecision, loanData: GetLoanApiResponse) => {
            if (loanData.redirectUrl) {
                setSelectedPlan(null);
                setDecisionData({
                    ...decisionDataRef.current!,
                    decision: SubmitApplicationDecision.Submitted,
                    loanId: secondLookLoanId,
                    approvedAmount:
                        loanData.requestedAmount != null && loanData.approvedAmount != null
                            ? Math.min(loanData.requestedAmount, loanData.approvedAmount)
                            : (loanData.requestedAmount ?? loanData.approvedAmount ?? null),
                    maxCreditAmount: loanData.approvedAmount ?? null,
                    externalLoanId: loanData.externalId ?? '',
                    redirectUrl: loanData.redirectUrl,
                    declineRedirectUrl: loanData.declineRedirectUrl ?? null,
                });
                setWorkflowStage('second-look-decision');
            } else if (decision === SubmitApplicationDecision.Approved) {
                setSelectedPlan(null);
                setDecisionData({
                    ...decisionDataRef.current!,
                    decision: SubmitApplicationDecision.Approved,
                    loanId: secondLookLoanId,
                    approvedAmount:
                        loanData.requestedAmount != null && loanData.approvedAmount != null
                            ? Math.min(loanData.requestedAmount, loanData.approvedAmount)
                            : (loanData.requestedAmount ?? loanData.approvedAmount ?? null),
                    maxCreditAmount: loanData.approvedAmount ?? null,
                    externalLoanId: loanData.externalId ?? '',
                });
                setWorkflowStage('second-look-decision');
            } else if (decision === SubmitApplicationDecision.Declined) {
                // Both lenders have now declined. Only the second-look decline details are merged
                // in: the decision, loan id, and external id still describe the first-look
                // application that the rest of the workflow keys off.
                setDecisionData({
                    ...decisionDataRef.current!,
                    secondLookDeclined: true,
                    declineRedirectUrl: loanData.declineRedirectUrl ?? null,
                });
            }
            setSecondLookDecision(decision);
        },
        [secondLookLoanId, setSelectedPlan, setDecisionData, setWorkflowStage],
    );

    const handleSecondLookTimeout = useCallback(() => {
        setSecondLookDecision(SubmitApplicationDecision.Declined);
        setDecisionData({
            ...decisionDataRef.current!,
            secondLookDeclined: true,
        });
    }, [setDecisionData]);

    // Second look decision polling, when we have it approved, we change workflow stage to second-look-decision
    useLoanStatusPolling({
        loanId: secondLookLoanId,
        onDecisionChange: handleSecondLookDecisionChange,
        onTimeout: handleSecondLookTimeout,
    });

    useEffect(() => {
        // For financing submissions, check loanId; for prequal submissions, check prequalId
        // If we have a decision, proceed regardless (decision might be set before ID is available)
        const hasFinancingId = !!decisionData?.loanId || !!loanId;
        const hasPrequalId = isHardPrequalification ? !!setupData?.prequalificationId : !!prequalId;
        if (!decisionData?.decision && !hasFinancingId && !hasPrequalId) {
            return;
        }

        if (decisionData?.decision === SubmitApplicationDecision.Approved) {
            setApplicationState('approved');
        } else if (decisionData?.decision === SubmitApplicationDecision.Declined) {
            setApplicationState('declined');

            if (
                secondLookIntegration.isEnabled &&
                tenantSettingsLoaded &&
                !isBelowWaterfallThreshold
            ) {
                setSecondLookState('sending');
                submitSecondLook();
            }
        } else if (decisionData?.decision === SubmitApplicationDecision.Submitted) {
            // No decision yet. Keep waiting.
        } else {
            setApplicationState('reviewing');
        }
    }, [
        decisionData?.decision,
        decisionData?.loanId,
        loanId,
        prequalId,
        isHardPrequalification,
        setupData?.prequalificationId,
        secondLookIntegration.isEnabled,
        tenantSettingsLoaded,
        isBelowWaterfallThreshold,
        submitSecondLook,
    ]);

    // Transition to decision workflow for providers that require it (e.g., Service Finance)
    useEffect(() => {
        // Only transition if provider is configured to use decision workflow
        if (!useDecisionWorkflow) {
            return;
        }

        // Don't transition if no decision yet
        if (
            !decisionData?.decision ||
            decisionData.decision === SubmitApplicationDecision.Submitted
        ) {
            return;
        }

        // Don't transition if second look is enabled and we're waiting for its decision
        // (but skip this wait if the amount is below the waterfall threshold)
        if (
            secondLookIntegration.isEnabled &&
            !isBelowWaterfallThreshold &&
            decisionData.decision === SubmitApplicationDecision.Declined
        ) {
            const secondLookTransientDecisions = [
                SubmitApplicationDecision.Approved,
                SubmitApplicationDecision.Review,
                SubmitApplicationDecision.Submitted,
            ];
            // Wait for the second-look decision if it's still in progress
            if (
                secondLookTransientDecisions.includes(secondLookDecision) &&
                secondLookState !== 'none'
            ) {
                return;
            }
        }

        // Transition to decision stage for approved applications
        if (decisionData.decision === SubmitApplicationDecision.Approved) {
            setWorkflowStage('decision');
        }
    }, [
        useDecisionWorkflow,
        decisionData?.decision,
        secondLookIntegration.isEnabled,
        isBelowWaterfallThreshold,
        secondLookDecision,
        secondLookState,
        setWorkflowStage,
    ]);

    // Check if this is a second-look approval (first-look declined, second-look approved)
    // This happens when the loanId matches the second-look loan id, meaning the approval
    // came from the second-look submission rather than the original GreenSky submission
    const isSecondLookApproval =
        decisionData?.decision === SubmitApplicationDecision.Approved &&
        secondLookLoanId != null &&
        decisionData?.loanId === secondLookLoanId;

    const getDecisionComponent = () => {
        const secondLookTransientDecisions = [
            SubmitApplicationDecision.Approved,
            SubmitApplicationDecision.Review,
            SubmitApplicationDecision.Submitted,
        ];
        if (decisionData?.decision === SubmitApplicationDecision.Approved) {
            return <Approved isSecondLookApproval={isSecondLookApproval} />;
        } else if (
            decisionData?.decision === SubmitApplicationDecision.Declined &&
            !secondLookTransientDecisions.includes(secondLookDecision)
        ) {
            return <Declined />;
        } else if (
            decisionData?.decision === SubmitApplicationDecision.Declined &&
            (!secondLookIntegration.isEnabled || isBelowWaterfallThreshold)
        ) {
            return <Declined />;
        } else if (decisionData?.decision === SubmitApplicationDecision.Review) {
            return <UnderReview />;
        } else if (decisionData?.decision === SubmitApplicationDecision.CounterOffer) {
            return <CounterOffer />;
        }
        return <Reviewing applicationState={applicationState} secondLookState={secondLookState} />;
    };

    const isApproved = decisionData?.decision === SubmitApplicationDecision.Approved;

    useEffect(() => {
        setShowApprovedFooter(isApproved && !useDecisionWorkflow);
        return () => setShowApprovedFooter(false);
    }, [isApproved, useDecisionWorkflow, setShowApprovedFooter]);

    return (
        <Flex
            direction="column"
            gap="6"
            alignItems="center"
            style={{ width: '100%', margin: '0 auto' }}
        >
            {isGreenSky && iovationComponent}
            <div style={{ flexGrow: 1, width: '100%' }}>{getDecisionComponent()}</div>
        </Flex>
    );
};

export const ReviewingWorkflow = () => <ReviewingWorkflowInner />;
